import React, { useState, useEffect, useRef } from 'react';
import { Phone, X, Grid, Hash, PhoneOff, User, Mic, MicOff, Volume2, VolumeX, Delete, ChevronDown } from 'lucide-react';
import axios from 'axios';
import plivoService from '../services/plivoService';
import { adminRequirementsAPI, pbxCallAPI, telephonyConfigAPI, pbxServerAPI } from '../services/api';
import { tokenStorage } from '../utils/tokenStorage';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { getDisplayPhoneNumber } from '../utils/numberMasking';
import { useClientRequirements } from '../hooks/useClientRequirements';
import './FloatingDialPad.css';

// ── Country Codes Database ───────────────────────────────────────────────────
const COUNTRY_CODES = [
    { code: '+1', country: 'United States', flag: '🇺🇸', iso: 'US' },
    { code: '+1', country: 'Canada', flag: '🇨🇦', iso: 'CA' },
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧', iso: 'GB' },
    { code: '+91', country: 'India', flag: '🇮🇳', iso: 'IN' },
    { code: '+86', country: 'China', flag: '🇨🇳', iso: 'CN' },
    { code: '+81', country: 'Japan', flag: '🇯🇵', iso: 'JP' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷', iso: 'KR' },
    { code: '+33', country: 'France', flag: '🇫🇷', iso: 'FR' },
    { code: '+49', country: 'Germany', flag: '🇩🇪', iso: 'DE' },
    { code: '+39', country: 'Italy', flag: '🇮🇹', iso: 'IT' },
    { code: '+34', country: 'Spain', flag: '🇪🇸', iso: 'ES' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱', iso: 'NL' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭', iso: 'CH' },
    { code: '+43', country: 'Austria', flag: '🇦🇹', iso: 'AT' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪', iso: 'SE' },
    { code: '+47', country: 'Norway', flag: '🇳🇴', iso: 'NO' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰', iso: 'DK' },
    { code: '+358', country: 'Finland', flag: '🇫🇮', iso: 'FI' },
    { code: '+48', country: 'Poland', flag: '🇵🇱', iso: 'PL' },
    { code: '+420', country: 'Czech Republic', flag: '🇨🇿', iso: 'CZ' },
    { code: '+36', country: 'Hungary', flag: '🇭🇺', iso: 'HU' },
    { code: '+40', country: 'Romania', flag: '🇷🇴', iso: 'RO' },
    { code: '+30', country: 'Greece', flag: '🇬🇷', iso: 'GR' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷', iso: 'TR' },
    { code: '+380', country: 'Ukraine', flag: '🇺🇦', iso: 'UA' },
    { code: '+7', country: 'Russia', flag: '🇷🇺', iso: 'RU' },
    { code: '+972', country: 'Israel', flag: '🇮🇱', iso: 'IL' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', iso: 'SA' },
    { code: '+971', country: 'United Arab Emirates', flag: '🇦🇪', iso: 'AE' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾', iso: 'MY' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬', iso: 'SG' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭', iso: 'TH' },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳', iso: 'VN' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩', iso: 'ID' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭', iso: 'PH' },
    { code: '+61', country: 'Australia', flag: '🇦🇺', iso: 'AU' },
    { code: '+64', country: 'New Zealand', flag: '🇳🇿', iso: 'NZ' },
    { code: '+55', country: 'Brazil', flag: '🇧🇷', iso: 'BR' },
    { code: '+54', country: 'Argentina', flag: '🇦🇷', iso: 'AR' },
    { code: '+56', country: 'Chile', flag: '🇨🇱', iso: 'CL' },
    { code: '+57', country: 'Colombia', flag: '🇨🇴', iso: 'CO' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽', iso: 'MX' },
];

const FloatingDialPad = () => {
    const { user, businessUnit, isAuthenticated } = useAuth();
    const { requirements: clientRequirements, loading: requirementsLoading } = useClientRequirements();
    const [isOpen, setIsOpen] = useState(false);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [displayNumber, setDisplayNumber] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES.find(c => c.iso === 'IN') || COUNTRY_CODES[0]);
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const [countrySearch, setCountrySearch] = useState('');
    const [callState, setCallState] = useState({
        state: 'idle', // idle, connecting, ringing, answered, ended, failed
        error: null,
        currentNumber: null,
        isLoggedIn: false
    });
    const [callDuration, setCallDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [showKeypad, setShowKeypad] = useState(true);
    const [assignedNumber, setAssignedNumber] = useState(process.env.REACT_APP_PLIVO_CALLER_ID || '');
    const [telephonyProvider, setTelephonyProvider] = useState(clientRequirements?.telephony_provider || 'plivo');
    const [pbxConfig, setPbxConfig] = useState({ pbx_api_url: clientRequirements?.pbx_api_url || '', pbx_api_key: clientRequirements?.pbx_api_key || '', pbx_extension: clientRequirements?.pbx_extension || '' });
    const [pbxCallState, setPbxCallState] = useState('idle');
    const [pbxCallSteps, setPbxCallSteps] = useState([]);
    const [pbxCallNumber, setPbxCallNumber] = useState(null);
    const telephonyProviderRef = useRef(clientRequirements?.telephony_provider || 'plivo');
    const pbxConfigRef = useRef({ pbx_api_url: clientRequirements?.pbx_api_url || '', pbx_api_key: clientRequirements?.pbx_api_key || '', pbx_extension: clientRequirements?.pbx_extension || '' });
    const [configLoadError, setConfigLoadError] = useState(null);

    const notifyLeadsRefresh = () => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('leadsUpdated'));
        }
    };

    const timerRef = useRef(null);
    const inputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const dropdownRef = useRef(null);

    const configLoadedRef = useRef(false);

    const getRbacRoleExtension = () => {
        try {
            const sessionRaw = sessionStorage.getItem('rbac_session');
            const session = sessionRaw ? JSON.parse(sessionRaw) : null;
            if (session?.extension_number) return session.extension_number;
            if (session?.role?.extension_number) return session.role.extension_number;

            const token = session?.token || tokenStorage.getApiAccessToken();
            if (!token) return null;

            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.extension_number || payload.role?.extension_number || payload.extension || payload.role?.extension || null;
        } catch (err) {
            console.error('FloatingDialPad: failed to resolve RBAC role extension', err);
            return null;
        }
    };

    const fetchTelephonyConfig = async () => {
        try {
            // Get RBAC token
            const token = tokenStorage.getApiAccessToken();

            if (!token) {
                console.warn('FloatingDialPad: No authentication token available');
                return;
            }

            // Fetch admin requirements which includes telephony config
            const res = await adminRequirementsAPI.getAny();
            const payload = res?.data?.data || res?.data || res;
            if (!payload) {
                console.warn('FloatingDialPad: No payload in requirements response');
                return;
            }

            const provider = payload.telephony_provider || 'plivo';
            const roleExtension = getRbacRoleExtension();
            const config = {
                pbx_api_url: payload.pbx_api_url || '',
                pbx_api_key: payload.pbx_api_key || payload.pbx_api_key || '',
                pbx_extension: roleExtension || payload.pbx_extension || ''
            };

            setTelephonyProvider(provider);
            telephonyProviderRef.current = provider;
            setPbxConfig(config);
            pbxConfigRef.current = config;
            setConfigLoadError(null);

            // Store admin id for server PBX calls
            if (payload.admin_id) {
                setPbxConfig(prev => ({ ...prev, admin_id: payload.admin_id }));
                pbxConfigRef.current = { ...pbxConfigRef.current, admin_id: payload.admin_id };
            }

            console.log('✅ FloatingDialPad: Telephony config loaded', { provider, pbx_extension: config.pbx_extension });
        } catch (err) {
            console.error('FloatingDialPad: Unable to load telephony config', err);
        }
    };

    const executePbxCall = async (phone) => {
        const config = pbxConfigRef.current;
        if (!config.pbx_api_url || !config.pbx_extension) {
            toast.error('PBX is not configured. Please check Requirements.');
            return false;
        }

        setPbxCallNumber(phone);
        setPbxCallState('initiating');
        setPbxCallSteps([
            { label: 'Call initiated', status: 'active' },
            { label: 'Connecting to PBX', status: 'pending' },
            { label: 'Ringing destination', status: 'pending' },
            { label: 'Call connected', status: 'pending' }
        ]);

        try {
            // Get RBAC token for authentication
            const token = tokenStorage.getApiAccessToken();
            if (!token) {
                toast.error('Authentication token not available. Please log in again.');
                setPbxCallState('failed');
                return false;
            }

            // Create axios instance with auth header
            const axiosInstance = axios.create({
                baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
                headers: { Authorization: `Bearer ${token}` }
            });

            // Call PBX through server endpoint
            const tokenPayload = (() => {
                try {
                    return JSON.parse(atob(token.split('.')[1]));
                } catch (e) {
                    return {};
                }
            })();
            const adminId = config.admin_id || (user && user.id) || tokenPayload.admin_id || tokenPayload.user_id || tokenPayload.id || null;
            const rbacRoleId = tokenPayload.role_id || tokenPayload.rbac_role_id || null;
            const roleExtension = getRbacRoleExtension();
            const extension = roleExtension || config.pbx_extension;
            const resp = await axiosInstance.post('/pbx/call/rbac', {
                adminId,
                extension,
                phone,
                rbac_role_id: rbacRoleId
            });

            const result = resp?.data || resp;

            if (!result || result.success === false) {
                setPbxCallState('failed');
                toast.error(result?.error || result?.message || 'PBX call failed.');
                console.error('PBX call failed:', result);
                return false;
            }

            setPbxCallState('connected');
            toast.success('PBX call initiated successfully.');
            console.log('✅ PBX call initiated:', { extension: config.pbx_extension, phone });
            notifyLeadsRefresh();
            return true;
        } catch (err) {
            setPbxCallState('failed');
            console.error('FloatingDialPad PBX call error:', err);
            const errorMsg = err.response?.data?.message || err.response?.data?.error || err.message || 'PBX call could not be started.';
            toast.error(errorMsg);
            return false;
        }
    };

    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const initPlivoServiceIfNeeded = async () => {
        if (plivoService.getCallState().isLoggedIn) {
            return true;
        }

        const plivoEndpoint = process.env.REACT_APP_PLIVO_ENDPOINT;
        const plivoPassword = process.env.REACT_APP_PLIVO_PASSWORD;
        if (!plivoEndpoint || !plivoPassword) {
            console.warn('FloatingDialPad: Cannot initialize Plivo, credentials missing');
            return false;
        }

        console.log('FloatingDialPad: Initializing Plivo for fallback');
        const initialized = plivoService.init((newState) => {
            setCallState(newState);
            if (newState.state === 'answered') {
                setCallDuration(0);
                timerRef.current = setInterval(() => {
                    setCallDuration(prev => prev + 1);
                }, 1000);
            } else if (['ended', 'failed', 'idle'].includes(newState.state)) {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                }
                if (newState.state === 'ended' || newState.state === 'failed') {
                    setTimeout(() => {
                        if (plivoService.getCallState().state === 'idle') {
                            setPhoneNumber('');
                            setCallDuration(0);
                            setIsMuted(false);
                        }
                    }, 3000);
                }
            }
        });

        if (!initialized) {
            console.warn('FloatingDialPad: Plivo service initialization failed');
            return false;
        }

        plivoService.login(plivoEndpoint, plivoPassword);
        let attempts = 0;
        while (attempts < 10 && !plivoService.getCallState().isLoggedIn) {
            await wait(300);
            attempts += 1;
        }
        return plivoService.getCallState().isLoggedIn;
    };

    const attemptPlivoCall = async (number, clientId) => {
        const ready = await initPlivoServiceIfNeeded();
        if (!ready) {
            toast.error('Plivo fallback is not available. Please check calling configuration.');
            return false;
        }

        plivoService.call(number, clientId);
        return true;
    };

    // Filter countries for dropdown
    const filteredCountries = countrySearch.trim()
        ? COUNTRY_CODES.filter(c =>
            c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
            c.code.includes(countrySearch) ||
            c.iso.toLowerCase().includes(countrySearch.toLowerCase())
        )
        : COUNTRY_CODES;

// Initialize Plivo Service
    useEffect(() => {
        const plivoEndpoint = process.env.REACT_APP_PLIVO_ENDPOINT;
        const plivoPassword = process.env.REACT_APP_PLIVO_PASSWORD;

       // Only fetch if authenticated
      const rbacSessionStr = sessionStorage.getItem('rbac_session');
        if (rbacSessionStr) {
        fetchTelephonyConfig().then(() => {
                if (telephonyProviderRef.current === 'pbx') {
                    setCallState(prev => ({ ...prev, state: 'idle', isLoggedIn: true }));
                }
            });
        }
        let initialized = false;
        // Skip Plivo init if provider is PBX
        if (telephonyProviderRef.current === 'pbx') {
            console.log('FloatingDialPad: PBX provider detected, skipping Plivo init');
            setCallState(prev => ({ ...prev, state: 'idle', isLoggedIn: true }));
        } else if (!plivoEndpoint || !plivoPassword) {
            console.warn('FloatingDialPad: Plivo credentials missing');
        } else {
            initialized = plivoService.init((newState) => {
                setCallState(newState);

                if (newState.state === 'answered') {
                    setCallDuration(0);
                    timerRef.current = setInterval(() => {
                        setCallDuration(prev => prev + 1);
                    }, 1000);
                } else if (['ended', 'failed', 'idle'].includes(newState.state)) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                        timerRef.current = null;
                    }
                    if (newState.state === 'ended' || newState.state === 'failed') {
                        setTimeout(() => {
                            if (plivoService.getCallState().state === 'idle') {
                                setPhoneNumber('');
                                setCallDuration(0);
                                setIsMuted(false);
                            }
                        }, 3000);
                    }
                }
            });

            if (initialized && !plivoService.getCallState().isLoggedIn) {
                plivoService.login(plivoEndpoint, plivoPassword);
            }
        }

        const handleRemoteCall = async (e) => {
            const { phoneNumber: numberToCall, clientId, skipDialpad = false } = e.detail;
            const overrideProvider = localStorage.getItem('_telephonyProvider');
            const effectiveProvider = overrideProvider || telephonyProviderRef.current || (pbxConfigRef.current?.pbx_extension ? 'pbx' : 'plivo');
            console.log('FloatingDialPad: effectiveProvider=', effectiveProvider, 'telephonyProviderRef=', telephonyProviderRef.current, 'pbxConfig=', pbxConfigRef.current);

            if (skipDialpad) {
                if (effectiveProvider === 'pbx') {
                    if (telephonyProviderRef.current === 'pbx' && (!pbxConfigRef.current?.pbx_extension || !pbxConfigRef.current?.pbx_api_url)) {
                        await fetchTelephonyConfig();
                    }
                    const success = await executePbxCall(numberToCall);
                    if (!success) {
                        console.warn('FloatingDialPad: PBX call failed, attempting Plivo fallback');
                        await attemptPlivoCall(numberToCall, clientId);
                    }
                    return;
                }

                if (await initPlivoServiceIfNeeded()) {
                    plivoService.call(numberToCall, clientId);
                } else {
                    toast.error('Not connected to calling service');
                }
                return;
            }

            setPhoneNumber(numberToCall);
            setIsOpen(true);

            const menuToggle = document.querySelector('.floating-menu-toggle');
            const menuContainer = document.querySelector('.floating-interaction-menu');
            if (menuContainer && !menuContainer.classList.contains('open')) {
                menuContainer.classList.add('open');
            }
            if (menuToggle && !menuToggle.classList.contains('open')) {
                menuToggle.classList.add('open');
            }

            // If PBX is selected but config is incomplete, reload requirements
            if (telephonyProviderRef.current === 'pbx' && (!pbxConfigRef.current?.pbx_extension || !pbxConfigRef.current?.pbx_api_url)) {
                await fetchTelephonyConfig();
            }
            if (effectiveProvider === 'pbx') {
                const success = await executePbxCall(numberToCall);
                if (!success) {
                    console.warn('FloatingDialPad: PBX call failed, attempting Plivo fallback');
                    await attemptPlivoCall(numberToCall, clientId);
                }
                return;
            }

            if (await initPlivoServiceIfNeeded()) {
                plivoService.call(numberToCall, clientId);
            } else {
                toast.error('Not connected to Plivo calling service');
            }
        };
        window.addEventListener('INITIATE_PLIVO_CALL', handleRemoteCall);

        const handleAdminRequirementsUpdate = (event) => {
            if (event.detail?.telephony_provider) {
                setTelephonyProvider(event.detail.telephony_provider);
                telephonyProviderRef.current = event.detail.telephony_provider;
            }
            if (event.detail?.pbx_api_url || event.detail?.pbx_api_key || event.detail?.pbx_extension) {
                const roleExtension = getRbacRoleExtension();
                setPbxConfig(prev => {
                    const next = {
                        pbx_api_url: event.detail.pbx_api_url || prev.pbx_api_url,
                        pbx_api_key: event.detail.pbx_api_key || prev.pbx_api_key,
                        pbx_extension: roleExtension || event.detail.pbx_extension || prev.pbx_extension
                    };
                    pbxConfigRef.current = next;
                    return next;
                });
            }
            fetchTelephonyConfig();
        };
        window.addEventListener('adminRequirementsUpdated', handleAdminRequirementsUpdate);

        // Fetch assigned number if user is authenticated
        if (isAuthenticated && user?.id) {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const rbacSessionStr = sessionStorage.getItem('rbac_session');
            let token = null;
            if (rbacSessionStr) {
                try {
                    const rbacSession = JSON.parse(rbacSessionStr);
                    token = rbacSession.token;
                } catch (e) {
                    console.error('Error parsing RBAC session:', e);
                }
            }

            if (tokenStorage.getApiAccessToken() && telephonyProvider !== 'pbx') {
                fetch(`${API_BASE_URL}/outbound/config/defaults?client_id=${user.id}`, {
                    headers: {
                        'Authorization': `Bearer ${tokenStorage.getApiAccessToken()}`
                    }
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.success && data.from_number) {
                            setAssignedNumber(data.from_number);
                            console.log('✅ Dial Pad: Assigned Plivo number loaded:', data.from_number);
                        }
                    })
                    .catch(err => console.error('❌ Dial Pad: Failed to fetch assigned number:', err));
            }
        }

        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowCountryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('INITIATE_PLIVO_CALL', handleRemoteCall);
            window.removeEventListener('adminRequirementsUpdated', handleAdminRequirementsUpdate);
            document.removeEventListener('mousedown', handleClickOutside);
            if (timerRef.current) clearInterval(timerRef.current);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        };
    }, [isAuthenticated, user]);

    useEffect(() => {
        telephonyProviderRef.current = telephonyProvider;
    }, [telephonyProvider]);

    useEffect(() => {
        if (!requirementsLoading && clientRequirements) {
            const provider = clientRequirements.telephony_provider || 'plivo';
            const roleExtension = getRbacRoleExtension();
            const config = {
                pbx_api_url: clientRequirements.pbx_api_url || '',
                pbx_api_key: clientRequirements.pbx_api_key || '',
                pbx_extension: roleExtension || clientRequirements.pbx_extension || ''
            };
            setTelephonyProvider(provider);
            telephonyProviderRef.current = provider;
            setPbxConfig(config);
            pbxConfigRef.current = config;
            if (provider === 'pbx') {
                setCallState(prev => ({ ...prev, state: 'idle', isLoggedIn: true }));
            }
        }
    }, [clientRequirements, requirementsLoading]);

    const toggleDialPad = () => {
        setIsOpen(!isOpen);
    };

    // ✅ Apply masking when user stops typing
    useEffect(() => {
        if (!phoneNumber) {
            setDisplayNumber('');
            return;
        }

        setDisplayNumber(phoneNumber);
    }, [phoneNumber]);
const handleNumberClick = (num) => {
        const newNumber = phoneNumber + num;
        setPhoneNumber(newNumber);
        setDisplayNumber(newNumber);
    };
   const handleDelete = () => {
        const newNumber = phoneNumber.slice(0, -1);
        setPhoneNumber(newNumber);
        setDisplayNumber(newNumber);
    };

    // ✅ Handle paste event to support pasting numbers
    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
       const cleanNumber = pastedText.replace(/[^0-9+*#]/g, '');
        setPhoneNumber(cleanNumber);
        setDisplayNumber(cleanNumber);
    };

    const handleCall = async () => {
        if (!phoneNumber) {
            toast.error('Please enter a phone number');
            return;
        }

        // Prefer the most recent provider value from the ref/localStorage
        const overrideProvider = localStorage.getItem('_telephonyProvider');
        const effectiveProvider = overrideProvider || telephonyProviderRef.current || telephonyProvider;
        console.log('FloatingDialPad: handleCall effectiveProvider=', effectiveProvider, 'telephonyProviderRef=', telephonyProviderRef.current);

        if (effectiveProvider === 'pbx') {
            const success = await executePbxCall(phoneNumber);
            if (!success) {
                console.warn('FloatingDialPad: PBX call failed, attempting Plivo fallback');
                const fallback = await attemptPlivoCall(phoneNumber, user.id);
                if (!fallback) {
                    return;
                }
            }
            return;
        }

        if (!callState.isLoggedIn) {
            const ready = await initPlivoServiceIfNeeded();
            if (!ready) {
                toast.error('Not connected to calling service');
                return;
            }
        }

        console.log('📞 Using Plivo number:', assignedNumber || 'Assigned Admin Number');
        plivoService.call(phoneNumber, user.id);
    };

    const handleHangup = () => {
        plivoService.hangup();
    };

    const toggleMute = () => {
        const newMuteState = !isMuted;
        plivoService.setMute(newMuteState);
        setIsMuted(newMuteState);
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    useEffect(() => {
        if (callState.state === 'ended' || callState.state === 'failed') {
            notifyLeadsRefresh();
        }
    }, [callState.state]);

    const isInCall = ['connecting', 'ringing', 'answered'].includes(callState.state);

    // ✅ Get display value based on masking requirement
    const getDisplayValue = () => {
        if (!phoneNumber) return '';
        return phoneNumber;
    };

    return (
        <div className="floating-dialpad-container">
            {/* Toggle Button */}
            <button
                className={`dialpad-toggle-btn ${isOpen ? 'active' : ''} ${isInCall ? 'in-call-pulse' : ''}`}
                onClick={toggleDialPad}
                title={isOpen ? "Close Dial Pad" : "Open Dial Pad"}
            >
                {isInCall ? <PhoneOff size={24} /> : <Phone size={24} />}
            </button>

            {/* Dial Pad Panel */}
            {isOpen && (
                <div className="dialpad-panel animate-slide-up">
                    <div className="dialpad-header">
                        <div className="header-info">
                            <span className="status-dot-container">
                                <span className={`status-dot ${callState.isLoggedIn ? 'online' : 'offline'}`}></span>
                                {callState.isLoggedIn ? 'Ready' : 'Connecting...'}
                            </span>
                            <h3>Dial Pad</h3>
                        </div>
                        <button className="close-btn" onClick={() => setIsOpen(false)}>
                            <X size={20} />
                        </button>
                    </div>

                    <div className="dialpad-display">
                        {/* Country Code Selector */}
                        <div className="dialpad-country-selector" style={{ width: '100%', marginBottom: '12px' }} ref={dropdownRef}>
                            <div style={{ position: 'relative' }}>
                                <button
                                    className="dialpad-country-button"
                                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                    disabled={isInCall}
                                >
                                    <span style={{ fontSize: '18px', marginRight: '6px' }}>{selectedCountry.flag}</span>
                                    <span style={{ flex: 1, textAlign: 'left', fontSize: '13px' }}>
                                        {selectedCountry.country}
                                    </span>
                                    <ChevronDown size={14} style={{ transform: showCountryDropdown ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }} />
                                </button>

                                {showCountryDropdown && (
                                    <div className="dialpad-country-dropdown">
                                        <input
                                            type="text"
                                            placeholder="Search country..."
                                            className="dialpad-country-search"
                                            value={countrySearch}
                                            onChange={(e) => setCountrySearch(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            autoFocus
                                        />
                                        <div className="dialpad-country-list">
                                            {filteredCountries.length > 0 ? (
                                                filteredCountries.map((country, idx) => (
                                                    <button
                                                        key={`${country.iso}-${idx}`}
                                                        className={`dialpad-country-option ${selectedCountry.iso === country.iso && selectedCountry.code === country.code ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedCountry(country);
                                                            setShowCountryDropdown(false);
                                                            setCountrySearch('');
                                                            inputRef.current?.focus();
                                                        }}
                                                    >
                                                        <span style={{ fontSize: '16px', marginRight: '6px' }}>{country.flag}</span>
                                                        <span style={{ flex: 1, textAlign: 'left' }}>
                                                            <strong>{country.code}</strong> {country.country}
                                                        </span>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="dialpad-country-empty">No countries found</div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Phone Number Input with Integrated Country Code Badge */}
                        <div className="number-input-container">
                            <div className={`dialpad-phone-input-wrapper ${isInCall ? 'disabled' : ''}`} style={{ pointerEvents: isInCall ? 'none' : 'auto', opacity: isInCall ? 0.6 : 1 }}>
                                <div className="dialpad-country-code-badge" onClick={() => !isInCall && setShowCountryDropdown(!showCountryDropdown)} title="Click to change country">
                                    {selectedCountry.code}
                                </div>
<input
                                    ref={inputRef}
                                    type="text"
                                    value={displayNumber}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;
                                        const clean = inputValue.replace(/[^0-9+*#]/g, '');

                                        setPhoneNumber(clean);
                                        setDisplayNumber(clean);
                                    }}                                    onPaste={handlePaste}
                                    placeholder="Enter number..."
                                    className="dialpad-number-display"
                                    readOnly={isInCall}
                                />
                            </div>
                            {!isInCall && phoneNumber && (
                                <button className="delete-btn" onClick={handleDelete}>
                                    <Delete size={18} />
                                </button>
                            )}
                        </div>


                        {isInCall && (
                            <div className="call-info">
                                <div className="call-status">
                                    {callState.state === 'connecting' && 'Connecting...'}
                                    {callState.state === 'ringing' && 'Ringing...'}
                                    {callState.state === 'answered' && 'Connected'}
                                </div>
                                {callState.state === 'answered' && (
                                    <div className="call-timer">{formatDuration(callDuration)}</div>
                                )}
                            </div>
                        )}

                        {callState.state === 'failed' && (
                            <div className="call-status error">Call Failed</div>
                        )}
                    </div>

                    {!isInCall ? (
                        <div className="keypad-grid">
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, '*', 0, '#'].map((num) => (
                                <button
                                    key={num}
                                    className="keypad-btn"
                                    onClick={() => handleNumberClick(num.toString())}
                                >
                                    {num}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="call-controls">
                            <button
                                className={`control-btn ${isMuted ? 'active' : ''}`}
                                onClick={toggleMute}
                                title={isMuted ? "Unmute" : "Mute"}
                            >
                                {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                                <span>{isMuted ? 'Muted' : 'Mute'}</span>
                            </button>

                            <button
                                className="control-btn"
                                onClick={() => setShowKeypad(!showKeypad)}
                            >
                                <Grid size={20} />
                                <span>Keypad</span>
                            </button>

                            <button className="control-btn">
                                <Volume2 size={20} />
                                <span>Speaker</span>
                            </button>
                        </div>
                    )}

                    <div className="dialpad-footer">
                        {isInCall ? (
                            <button className="action-btn hangup-btn" onClick={handleHangup}>
                                <PhoneOff size={24} />
                                <span>End Call</span>
                            </button>
                        ) : (
                          <button
                                className={`action-btn call-btn ${!phoneNumber ? 'disabled' : ''}`}
                                onClick={handleCall}
                                disabled={!phoneNumber}
                            >
                                <Phone size={24} />
                                <span>Call</span>
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FloatingDialPad;
